import { useQuery } from '@tanstack/react-query';
import { UserKeys, getAllResourceTemplates } from '../../../api/users-api';
import { useMemo, useState } from 'react';
import { ResourceTemplateModel } from '../../../../types';
import DeleteResourceTemplateDialog from './delete-resource-template-dialog';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { pagesConfig } from '../../../shared/pages-config';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-pro';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import { useNavigate } from 'react-router-dom';
import ResourceTemplatesGridToolbar from './resource-templates-grid-toolbar';
import SharedDataGridClient from '../../../shared/shared-data-grid-client';
import { useToolbar } from '../../../shared/shared-data-grid-toolbar-helper';

import Tooltip from '@mui/material/Tooltip';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import Feed from '@mui/icons-material/Feed';

export default function ResourceTemplatesGrid() {
  const { calendarRoles, changeLogRoles, viewType } = useUserSettings();
  const navigate = useNavigate();

  const [selectedTemplate, setSelectedTemplate] =
    useState<ResourceTemplateModel>();
  const [openDeleteTemplate, setOpenDeleteTemplate] = useState(false);

  const {
    data: templates,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [UserKeys.CalendarResourceTemplates],
    queryFn: getAllResourceTemplates,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('resource templates'),
    },
  });

  const columns: GridColDef<ResourceTemplateModel>[] = useMemo(
    () => [
      {
        field: 'dateCreated',
        headerName: 'Created Date',
        flex: 1,
        type: 'dateTime',
        valueGetter: (value: number) => {
          const date = new Date(value);
          return date;
        },
        valueFormatter: (params: Date) => {
          return params.toLocaleString();
        },
      },
      {
        field: 'name',
        headerName: 'Template Name',
        flex: 1,
      },
      {
        field: 'desc',
        headerName: 'Description',
        flex: 1,
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        getActions: (params) => {
          const buttonArray = [];
          if (
            isAbleToSeeUIElement(
              calendarRoles,
              CalendarRoles.EditResourceTemplate.value
            )
          ) {
            buttonArray.push(
              <GridActionsCellItem
                label="Edit Template"
                icon={
                  <Tooltip arrow title="Edit">
                    <Edit />
                  </Tooltip>
                }
                onClick={() => {
                  navigate({
                    pathname: `/${
                      pagesConfig.resourceTemplates.link
                    }/${pagesConfig.editResourceTemplate.link.replace(
                      ':id',
                      params.row.id.toString()
                    )}`,
                  });
                }}
              />
            );
          }
          if (
            isAbleToSeeUIElement(
              calendarRoles,
              CalendarRoles.DeleteResourceTemplate.value
            )
          ) {
            buttonArray.push(
              <GridActionsCellItem
                label="Delete"
                icon={
                  <Tooltip arrow title="Delete">
                    <Delete />
                  </Tooltip>
                }
                onClick={() => {
                  setSelectedTemplate(params.row);
                  setOpenDeleteTemplate(true);
                }}
              />
            );
          }
          return buttonArray;
        },
      },
    ],
    [calendarRoles, navigate]
  );

  const defaultGridState = {
    sorting: {
      sortModel: [
        {
          field: 'dateCreated',
          sort: 'desc',
        },
      ],
    },
  };

  const toolbarProps = useMemo(
    () => ({
      calendarRoles,
    }),
    [calendarRoles]
  );

  return (
    <>
      <SharedDataGridClient
        key="resource-templates-grid"
        slots={{
          toolbar: useToolbar(ResourceTemplatesGridToolbar, toolbarProps),
          ...createGridOverlays('Resource Templates', <Feed />),
        }}
        disableRowSelectionOnClick
        getRowId={(row) => row.id}
        rows={templates || []}
        loading={isLoading || isFetching}
        defaultGridState={defaultGridState}
        gridKey={viewType.resourceTemplatesGridStates?.activeGridState}
        columns={columns}
        gridStateKey="resourceTemplatesGridStates"
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'default',
          },
        }}
      />
      {selectedTemplate && openDeleteTemplate ? (
        <DeleteResourceTemplateDialog
          open={openDeleteTemplate}
          handleClose={() => setOpenDeleteTemplate(false)}
          selectedTemplate={selectedTemplate}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
    </>
  );
}
